import type { MouseEvent as ReactMouseEvent } from 'react';

import { useStalePageCheck } from 'platform/hooks/useReloadStalePage';
import { useToPath } from 'platform/preserveQueryParams';
import { useHref, useLinkClickHandler } from 'react-router';
import { trackNavigationEvent } from 'shared/hooks/useTrackPerformance';

import type { SharedCTAProps } from './types';

type ActionProps = SharedCTAProps;
type ActionHref = Pick<ActionProps, 'href'>['href'];

/**
 * Function that checks if the href is the same origin as the current page.
 * This is useful for determining if a link should be opened via an anchor tag or a router link.
 */
const isSameOrigin = (href: string): boolean => {
  // Shortcircuit for relative URLs
  if (href.startsWith('/')) {
    return true;
  }

  try {
    const hrefUrl = new URL(href);
    return hrefUrl.origin === window.location.origin;
  } catch {
    return false;
  }
};

const isString = (value: unknown): value is string => typeof value === 'string';

const isInternalCTA = (to: ActionHref): to is NonNullable<ActionHref> =>
  Boolean(to && (!isString(to) || isSameOrigin(to)));

const getRouterHookTarget = (to: ActionHref): NonNullable<ActionHref> => {
  if (!isInternalCTA(to)) {
    return '/';
  }

  return to;
};

/**
 * Resolves an internal CTA `href` into a react-router `href` + press handler. The
 * press handler runs the stale-page/auth check, tracks navigation, invokes the
 * caller's `onPress`, then drives react-router's client-side navigation. External
 * or `onPress`-only CTAs pass through unchanged.
 */
export function useInternalCTAProps<
  TPressEvent extends ReactMouseEvent<HTMLAnchorElement> = ReactMouseEvent<HTMLAnchorElement>,
>({
  href: to,
  customDisallowedSearchParams,
  onPress: handlePress,
  replace,
  state,
  target,
}: Pick<ActionProps, 'href' | 'customDisallowedSearchParams' | 'replace' | 'state' | 'target'> & {
  onPress?: ((event: TPressEvent) => void) | null;
}): {
  href?: string;
  onPress?: ((event: TPressEvent) => void) | null;
} {
  const checkStalePageAndAuth = useStalePageCheck();

  const getToPath = useToPath(customDisallowedSearchParams);
  const isInternalLink = isInternalCTA(to);
  const toPath = getToPath(getRouterHookTarget(to));
  const href = useHref(toPath);
  const handleRouterClick = useLinkClickHandler(toPath, {
    replace,
    state,
    target,
  });

  // If `to` is undefined or points to a link external to the app, return passed-in props
  // (Can't put this earlier in the function because hooks can't be called conditionally)
  if (!isInternalLink) {
    return { href: isString(to) ? to : undefined, onPress: handlePress };
  }

  const onPress = (event: TPressEvent) => {
    void checkStalePageAndAuth();

    trackNavigationEvent();
    handlePress?.(event);

    if (!event.defaultPrevented) {
      handleRouterClick(event);
    }
  };

  return { href, onPress };
}
